'use client'

import { PlayArrow } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogTitle, Typography } from '@mui/material'
import { useLoginInfo } from 'app/_hooks'
import { Route } from 'next'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useVideoDetail } from '~/features/video_detail/hooks/useVideoDetail'
import { useBeforeLoginSessionStorage } from '~/hooks/useBeforeLoginSessionStorage'
import { sendLog } from '~/libs/ga'

type Props = {
  videoId: string
  playlistId?: string
  fullWidth?: boolean
  small?: boolean
  onPlay?: () => void
}

export const PlayButton = ({ videoId, playlistId, fullWidth, small, onPlay }: Props) => {
  const { t } = useTranslation()
  const loginInfo = useLoginInfo()
  const { setBeforeLoginPage } = useBeforeLoginSessionStorage()

  const { push } = useRouter()
  const [openUnavailableDialog, setOpenUnavailableDialog] = useState(false)
  const { unavailableReason, data: videoData } = useVideoDetail(videoId)
  const data: { title: string; buttonTitle: string; href: Route } | undefined = useMemo(() => {
    switch (loginInfo.status) {
      case 'notSignedIn':
        return {
          title: t('login_to_watch_the_full_video'),
          buttonTitle: t('login'),
          href: '/login',
        }
      case 'noProfile':
        return {
          title: t('create_a_profile_to_watch_the_full_video'),
          buttonTitle: t('create_profile'),
          href: '/onboarding',
        }
      case 'signedIn':
        switch (unavailableReason) {
          case 'REQUIRE_AUTH':
            return { title: t('not_logged_in'), buttonTitle: t('login'), href: '/login' }
          case 'ALREADY_WITHDRAWAL':
            return {
              title: t('already_canceled'),
              buttonTitle: t('REACTIVATE'),
              href: '/profile/reactivate',
            }
          case 'REQUIRE_RESUBSCRIPTION':
            return {
              title: t('already_canceled'),
              buttonTitle: t('REACTIVATE'),
              href: '/profile/reactivate',
            }
          case 'REQUIRE_SUBSCRIPTION':
            return {
              title: t('please_choose_the_monthly_plan_to_watch_the_full_video'),
              buttonTitle: t('SUBSCRIBE'),
              href: '/profile/payment/new',
            }
          case 'INVALID_CHARGE':
            return {
              title: t('your_payment_information_is_invalid'),
              buttonTitle: t('UPDATE'),
              href: '/profile/payment/edit',
            }
        }
      default:
        return undefined
    }
  }, [loginInfo, t, unavailableReason])

  const watchHref = (
    playlistId ? `/videos/${videoId}/watch?playlistId=${playlistId}` : `/videos/${videoId}/watch`
  ) as Route

  const pathname = usePathname()
  useEffect(() => {
    sendLog({ screen: 'play-button', action: 'view', value: JSON.stringify({ pathname }) })
  }, [pathname])

  return (
    <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
      <Button
        disabled={videoData?.originalVideo?.status === 'upcoming'}
        sx={{
          position: 'relative',
          height: '100%',
          width: fullWidth ? '100%' : 'auto',
          minHeight: 'auto',
          display: 'flex',
          gap: '4px',
          padding: small ? '6px 12px' : '12px 8px',
          border: 'none',
          borderRadius: '6px',
          background:
            videoData?.originalVideo?.status === 'upcoming'
              ? ''
              : 'linear-gradient(97deg, #5C1833 0%, #700025 100%)',
          boxShadow: '0 2px 6px 2px rgba(0, 0, 0, 0.15), 0 1px 2px 0 rgba(0, 0, 0, 0.30)',
          color: '#FFFFFF',
          cursor: 'pointer',
        }}
        onClick={() => {
          if (!isMobile) {
            if (unavailableReason) {
              sendLog({
                screen: 'play-button',
                action: 'view-unavailable',
                value: JSON.stringify({ pathname }),
              })
              setOpenUnavailableDialog(true)
            } else {
              sendLog({
                screen: 'PlayButton',
                action: 'play',
                value: JSON.stringify({
                  videoId,
                  fullWidth,
                  small,
                }),
              })
              sendLog({
                screen: 'play-button',
                action: 'play',
                value: JSON.stringify({ pathname }),
              })
              push(watchHref)
              onPlay?.()
            }
          }
        }}
        onTouchEnd={(e) => {
          if (isMobile) {
            e.preventDefault() // for preventing backdropClick
            if (unavailableReason) {
              sendLog({
                screen: 'play-button',
                action: 'view-unavailable',
                value: JSON.stringify({ pathname }),
              })
              setOpenUnavailableDialog(true)
            } else {
              sendLog({
                screen: 'play-button',
                action: 'play',
                value: JSON.stringify({ pathname }),
              })
              push(watchHref)
              onPlay?.()
            }
          }
        }}
      >
        <PlayArrow sx={{ width: '22px', height: '22px' }} />
        <Typography variant='body1' fontSize={small ? 12 : 14}>
          {videoData?.originalVideo?.status === 'upcoming'
            ? t('upcoming_release')
            : t('Start Movie')}
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '6px',
            backgroundColor: 'rgba(255, 255, 255, 0)',
            transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
            ':hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
            },
          }}
        />
      </Button>
      {data && (
        <Dialog
          open={openUnavailableDialog}
          onClose={() => {
            sendLog({
              screen: 'play-button',
              action: 'view-unavailable-close',
              value: JSON.stringify({ pathname }),
            })
            setOpenUnavailableDialog(false)
          }}
        >
          <DialogTitle>{data.title}</DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                sendLog({
                  screen: 'play-button',
                  action: 'view-unavailable-cancel',
                  value: JSON.stringify({ pathname }),
                })
                setOpenUnavailableDialog(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={() => {
                sendLog({
                  screen: 'play-button',
                  action: 'view-unavailable-confirm',
                  value: JSON.stringify({ pathname, buttonTitle: data.buttonTitle }),
                })
                if (data.href === '/login') {
                  setBeforeLoginPage(`/videos/${videoId}`)
                }
                push(data.href)
              }}
              variant='contained'
              color='primary'
            >
              {data.buttonTitle}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}
